import { useRef, type ReactNode } from 'react';
import type {
  InfiniteData,
  UseInfiniteQueryResult,
} from '@tanstack/react-query';

import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@design-system/empty';
import { Skeleton } from '@design-system/skeleton';
import { nearEdge } from '@utils/near-edge';
import { useFillViewport } from '@hooks/use-fill-viewport';
import type { TApiResponse } from '@shared/types';

const LOAD_MORE_PX = 200;

/**
 * The scrolling body of a content tab: skeleton, error, empty state or the
 * rows, with the next page fetched as the reader nears the bottom.
 */
export function ContentList<T extends { id: number }>({
  query,
  searching,
  renderItem,
  emptyIcon,
  emptyTitle,
  emptyDescription,
}: {
  query: UseInfiniteQueryResult<InfiniteData<TApiResponse<T>>, Error>;
  /** A search term is active, so an empty result is "no match" not "nothing yet". */
  searching: boolean;
  renderItem: (item: T) => ReactNode;
  emptyIcon: ReactNode;
  emptyTitle: string;
  emptyDescription: string;
}) {
  const rows = query.data?.pages.flatMap((page) => page.results) ?? [];
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadMore = () => void query.fetchNextPage();
  useFillViewport(
    scrollRef,
    query.hasNextPage,
    query.isFetchingNextPage,
    loadMore,
    rows.length
  );

  return (
    <div
      ref={scrollRef}
      data-slot='content-list'
      className='flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 pb-4'
      onScroll={(event) => {
        if (!query.hasNextPage || query.isFetchingNextPage) return;
        if (nearEdge(event.currentTarget, 'bottom', LOAD_MORE_PX)) loadMore();
      }}
    >
      {query.isPending && (
        <>
          <Skeleton className='h-[72px] w-full rounded-xl' />
          <Skeleton className='h-[72px] w-full rounded-xl' />
          <Skeleton className='h-[72px] w-full rounded-xl' />
        </>
      )}
      {query.isError && (
        <p
          role='alert'
          className='rounded-[10px] bg-red-100 px-3 py-1.5 text-xs text-red-500'
        >
          Couldn't load this list. Please try again.
        </p>
      )}
      {!query.isPending && !query.isError && rows.length === 0 && (
        <Empty className='h-full border-none'>
          <EmptyHeader>
            <EmptyMedia variant='icon'>{emptyIcon}</EmptyMedia>
            <EmptyTitle>{searching ? 'No results' : emptyTitle}</EmptyTitle>
            <EmptyDescription>
              {searching ? 'Nothing matches your search.' : emptyDescription}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
      {rows.map(renderItem)}
      {query.isFetchingNextPage && (
        <Skeleton className='h-[72px] w-full rounded-xl' />
      )}
    </div>
  );
}
